import Input from '@/components/Approval/LeftContainer/Input';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';
import { useGetApprovalAllType } from '@/hooks/apiHooks';
import { FormProvider, useFormContext } from 'react-hook-form';
import {
  Layout,
  ResultCount,
} from '@/components/Approval/LeftContainer/CategoryFilter/styles';
import CheckBox from '@/components/Approval/LeftContainer/CategoryFilter/CheckBox';
import { ApprovalType } from '@/types/api';
import { useTranslation } from 'react-i18next';
import { EventPageAreaType, GTMEventName } from '@/constant/gtm';
import { useGTMEventTracker } from '@/hooks/gtm/useGTMEventTracker';

type P = {
  pageArea: EventPageAreaType;
  filterName: 'types';
  handleChange: (arg: ApprovalType) => void;
};

const CategorySearch = ({ pageArea, filterName, handleChange }: P) => {
  const eventTracker = useGTMEventTracker(pageArea);
  const [cursor, setCursor] = useState(-1);
  const [isMovingMouse, setIsMovingMouse] = useState(false);
  // 첫 ArrowDown 은 무조건 맨 위부터. 검색/마우스 변경 시 리셋
  const navStartedRef = useRef(false);
  const lastKeyTsRef = useRef(0);

  const methods = useFormContext();
  const searchString = methods.watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);

  const { t } = useTranslation();
  const scrollLayout = useRef<HTMLDivElement>(null);

  const { data } = useGetApprovalAllType();

  useEffect(() => {
    setCursor(-1);
    navStartedRef.current = false;
    if (debouncedValue.length > 0) {
      eventTracker(GTMEventName.searchKeyword, {
        area_name: '양식',
        keyword: debouncedValue,
      });
    }
  }, [debouncedValue]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const haveResult = searchResult.length > 0;
    if (!haveResult) return;
    if (event.timeStamp === lastKeyTsRef.current) return; // 중복 이벤트 무시
    lastKeyTsRef.current = event.timeStamp;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsMovingMouse(false);
        if (!navStartedRef.current) {
          navStartedRef.current = true;
          setCursor(0);
        } else {
          setCursor((prev) => Math.min(prev + 1, searchResult.length - 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsMovingMouse(false);
        navStartedRef.current = true;
        setCursor((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        handleChange(searchResult[cursor]);
        break;
      case 'Escape':
        setCursor(-1);
        methods.resetField('keyword');
        break;
    }
  };

  const searchResult = debouncedValue
    ? data?.filter((res) =>
        res.name.toUpperCase().includes(debouncedValue.toUpperCase()),
      ) ?? []
    : [];

  const handleMouseMove = (index: number) => {
    setIsMovingMouse(true);
    navStartedRef.current = false;
    setCursor(index);
  };

  useEffect(() => {
    !isMovingMouse &&
      scrollLayout.current?.scrollTo({ top: cursor * 37, behavior: 'smooth' });
  }, [isMovingMouse, cursor]);

  return (
    <FormProvider {...methods}>
      <Input
        value={'keyword'}
        placeholder={'찾으시는 양식을 검색해 주세요'}
        handleKeyDown={handleKeyDown}
        onBlur={() => setCursor(-1)}
        bleed
      >
        <div style={{ position: 'relative', width: '100%', zIndex: '1' }}>
          {debouncedValue && (
            <Layout ref={scrollLayout}>
              <ResultCount>
                {t('검색결과')} <b>{searchResult.length}</b>
                {t('건')}
              </ResultCount>
              {searchResult.length > 0 ? (
                searchResult.map((res, idx) => (
                  <CheckBox
                    key={`search-${res.type}`}
                    filterName={filterName}
                    isTarget={idx === cursor}
                    index={idx}
                    onMouseMove={handleMouseMove}
                    handleChange={handleChange}
                    keyword={debouncedValue}
                    {...res}
                  />
                ))
              ) : (
                <div style={{ color: 'var(--dk-text-dim, #B7BBC2)', padding: '4px' }}>
                  {t('일치하는 결과가 없어요.')}
                  <br />
                  {t('검색어를 다시 한번 확인해 주세요.')}
                </div>
              )}
            </Layout>
          )}
        </div>
      </Input>
    </FormProvider>
  );
};

export default CategorySearch;
